import styled from '@emotion/styled';
import * as mcpApi from '@shared/api/mcp/mcpServersApi';
import * as skillsApi from '@shared/api/skills/skillsApi';
import Button from '@shared/components/core/button/CoreButton';
import IconsButton from '@shared/components/core/button/IconsButton';
import {
    DialogTitleWrap,
    DisplayDialogTitle,
    StyledDialogContent,
    ThemedDialog,
} from '@shared/components/core/dialog/CoreDialogStyles';
import StyledTextField from '@shared/components/core/field/StyledTextField';
import RateReviewFields from '@shared/components/core/rating/RateReviewFields';
import RatingSummary, { IRatingStats } from '@shared/components/core/rating/RatingSummary';
import { BodySmall, TitleXSmall } from '@shared/components/core/text/Typography';
import SpacedLoader from '@shared/components/loading/SpacedLoader';
import { useAlertsStore } from '@shared/hooks/state/AlertsContainer';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ExtensionKind } from './OrgExtensionsManager';

/** A comment normalized across the skill/connector twins for rendering. */
interface FeedbackComment {
    id: string;
    body?: string;
    authorName?: string;
    isDeleted: boolean;
    createdDate: string;
    replies: FeedbackComment[];
}

interface IExtensionFeedbackDialogProps {
    organizationId: string;
    kind: ExtensionKind;
    extensionId: string;
    title: string;
    initialRating?: number;
    /** Aggregate figures for the strip above the review fields. Omit to hide it. */
    stats?: IRatingStats;
    onClose: () => void;
}

/**
 * Org-internal rating + comments for a single skill/connector. Members star-rate and discuss
 * extensions their org shares; everything is scoped to the organization.
 */
export default function ExtensionFeedbackDialog({
    organizationId,
    kind,
    extensionId,
    title,
    initialRating,
    stats,
    onClose,
}: Readonly<IExtensionFeedbackDialogProps>) {
    const { t } = useTranslation('common');
    const isSkills = kind === 'skills';

    const [rating, setRating] = useState<number>(initialRating ?? 0);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [comments, setComments] = useState<FeedbackComment[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [newComment, setNewComment] = useState('');
    const [replyTo, setReplyTo] = useState<string | null>(null);
    const [replyBody, setReplyBody] = useState('');

    const loadComments = async () => {
        setIsLoading(true);
        try {
            const result = isSkills
                ? await skillsApi.getSkillComments(extensionId, organizationId)
                : await mcpApi.getMcpServerComments(extensionId, organizationId);
            if (result.resultType !== 'Ok' || !result.data) throw new Error('load failed');
            setComments(result.data as unknown as FeedbackComment[]);
        } catch {
            useAlertsStore.getState().showError(t('organization.governance.extensions.feedback.loadError'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadComments();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [extensionId, kind]);

    const submitRating = async (nextRating: number) => {
        try {
            const request = { rating: nextRating };
            const result = isSkills
                ? await skillsApi.rateSkill(extensionId, organizationId, request)
                : await mcpApi.rateMcpServer(extensionId, organizationId, request);
            if (result.resultType !== 'Ok') throw new Error('rate failed');
            setRating(nextRating);
        } catch {
            useAlertsStore.getState().showError(t('organization.governance.extensions.feedback.rateError'));
        }
    };

    const addComment = async (body: string, parentCommentId?: string) => {
        const trimmed = body.trim();
        if (!trimmed) return;
        try {
            const request = { body: trimmed, parentCommentId };
            const result = isSkills
                ? await skillsApi.createSkillComment(extensionId, organizationId, request)
                : await mcpApi.createMcpServerComment(extensionId, organizationId, request);
            if (result.resultType !== 'Ok') throw new Error('comment failed');
            setNewComment('');
            setReplyTo(null);
            setReplyBody('');
            await loadComments();
        } catch {
            useAlertsStore.getState().showError(t('organization.governance.extensions.feedback.commentError'));
        }
    };

    /**
     * One action for the whole review: save the score, then post the comment if one was written.
     * Replaces the old split flow, where a star click saved silently and the comment had its own
     * Send button.
     */
    const submitReview = async () => {
        setIsSubmitting(true);
        try {
            if (rating > 0) await submitRating(rating);
            if (newComment.trim()) await addComment(newComment);
        } finally {
            setIsSubmitting(false);
        }
    };

    const deleteComment = async (commentId: string) => {
        try {
            const result = isSkills
                ? await skillsApi.deleteSkillComment(commentId)
                : await mcpApi.deleteMcpServerComment(commentId);
            if (result.resultType !== 'Ok') throw new Error('delete failed');
            await loadComments();
        } catch {
            useAlertsStore.getState().showError(t('organization.governance.extensions.feedback.deleteError'));
        }
    };

    const renderComment = (comment: FeedbackComment, depth = 0) => (
        <CommentItem key={comment.id} $depth={depth}>
            <CommentHeader>
                <TitleXSmall>
                    {comment.authorName || t('organization.governance.extensions.feedback.member')}
                </TitleXSmall>
                {!comment.isDeleted && (
                    <IconsButton
                        label={t('delete')}
                        startIcon="close"
                        iconSize={12}
                        hideBorder
                        $color="simple"
                        onClick={() => deleteComment(comment.id)}
                    />
                )}
            </CommentHeader>
            <CommentBody $deleted={comment.isDeleted}>
                {comment.isDeleted ? t('organization.governance.extensions.feedback.deleted') : comment.body}
            </CommentBody>
            {depth === 0 && !comment.isDeleted && (
                <ReplyLinkWrap>
                    <Button
                        $color="text"
                        $size="small"
                        onClick={() => {
                            setReplyTo(replyTo === comment.id ? null : comment.id);
                            setReplyBody('');
                        }}>
                        {t('organization.governance.extensions.feedback.reply')}
                    </Button>
                </ReplyLinkWrap>
            )}
            {replyTo === comment.id && (
                <ReplyRow>
                    <StyledTextField
                        fullWidth
                        size="small"
                        value={replyBody}
                        placeholder={t('organization.governance.extensions.feedback.replyPlaceholder')}
                        onChange={(event) => setReplyBody(event.target.value)}
                    />
                    <Button $size="small" $color="secondary" onClick={() => addComment(replyBody, comment.id)}>
                        {t('send')}
                    </Button>
                </ReplyRow>
            )}
            {comment.replies?.map((reply) => renderComment(reply, depth + 1))}
        </CommentItem>
    );

    return (
        <ThemedDialog open fullWidth maxWidth="sm" scroll="paper" onClose={onClose}>
            <DialogTitleWrap>
                <DisplayDialogTitle>{title}</DisplayDialogTitle>
                <IconsButton label={t('cancel')} startIcon="closeX" $color="accent" onClick={onClose} />
            </DialogTitleWrap>
            <StyledDialogContent>
                {stats && (
                    <StatsStrip>
                        <RatingSummary {...stats} />
                    </StatsStrip>
                )}

                <RateReviewFields
                    rating={rating}
                    onRatingChange={setRating}
                    comment={newComment}
                    onCommentChange={setNewComment}
                    disabled={isSubmitting}
                />
                <ReviewActions>
                    <Button $color="primary" disabled={rating === 0 || isSubmitting} onClick={submitReview}>
                        {t('rating.review.submit')}
                    </Button>
                </ReviewActions>

                <Section>
                    <TitleXSmall>{t('organization.governance.extensions.feedback.comments')}</TitleXSmall>
                    {isLoading ? (
                        <SpacedLoader />
                    ) : comments.length === 0 ? (
                        <BodySmall>{t('organization.governance.extensions.feedback.noComments')}</BodySmall>
                    ) : (
                        <CommentList>{comments.map((comment) => renderComment(comment))}</CommentList>
                    )}
                </Section>
            </StyledDialogContent>
        </ThemedDialog>
    );
}

const Section = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.5rem;
    margin-top: 1rem;
`;

const StatsStrip = styled.div`
    padding: 0.75rem 0;
    margin: 0.5rem 0;
    border-bottom: 1px solid ${(props) => props.theme.color.card};
`;

const ReviewActions = styled.div`
    display: flex;
    justify-content: flex-end;
    margin-top: 0.75rem;
`;

const CommentList = styled.div`
    display: flex;
    flex-direction: column;
    gap: 0.75rem;
    margin-top: 0.5rem;
`;

const CommentItem = styled.div<{ $depth: number }>`
    display: flex;
    flex-direction: column;
    gap: 0.2rem;
    padding-left: ${(props) => (props.$depth > 0 ? '1rem' : '0')};
    border-left: ${(props) => (props.$depth > 0 ? `2px solid ${props.theme.color.border}` : 'none')};
`;

const CommentHeader = styled.div`
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 0.5rem;
`;

const CommentBody = styled(BodySmall)<{ $deleted?: boolean }>`
    white-space: pre-wrap;
    font-style: ${(props) => (props.$deleted ? 'italic' : 'normal')};
    color: ${(props) => (props.$deleted ? props.theme.color.textBody : props.theme.color.text)};
`;

const ReplyLinkWrap = styled.div`
    align-self: flex-start;
    margin-top: 0.1rem;
`;

const ReplyRow = styled.div`
    display: flex;
    align-items: flex-start;
    gap: 0.5rem;
    margin-top: 0.25rem;
`;
